import React, { useEffect, useState, useCallback, useRef, forwardRef } from "react";
import axios from 'axios';
import { useTranslation } from "react-i18next";
import { useNavigate } from 'react-router-dom';
import { Doughnut } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    ArcElement,
    Tooltip,
    Legend,
} from 'chart.js';
import { formatToIndianRupee } from "../../utility/FormatToIndianRupee";
import { CREDIT_API_URL, TRUSTPAISA_URL, CASHE_URL } from "../../Constants";
import { Container, Row, Col, Form, OverlayTrigger, Tooltip as BSTooltip } from 'react-bootstrap';
import OtpVerificationModal from '../../utility/OtpVerificationModal';
import NotificationPopup from '../../utility/NotificationPopup';
import useApplicationStore from '../../store/applicationStore';
import './EmiSection.css';

ChartJS.register(ArcElement, Tooltip, Legend);

const EmiSection = forwardRef(({ productType }, ref) => {
    const { t } = useTranslation('homepage');
    const navigate = useNavigate();
    const { setLeadData: setStoreLeadData, setCurrentStep: setStoreCurrentStep } = useApplicationStore();

    // Calculator Section
    const [loanFactorApiResponse, setLoanFactorApiResponse] = useState({
        minLoanAmount: 0,
        maxLoanAmount: 0,
        minROI: 0,
        maxROI: 0,
        minTenure: 0,
        maxTenure: 0,
    });
    const [loanAmount, setLoanAmount] = useState(0);
    const [interestRate, setInterestRate] = useState(0);
    const [loanTenure, setLoanTenure] = useState(0);
    const [apiResponse, setApiResponse] = useState({
        emi: 0,
        totalPayment: 0,
        totalInterest: 0,
    });
    const [isApiCallInProgress, setIsApiCallInProgress] = useState(false);
    const debounceTimeout = useRef(null);
    const [interestRateText, setInterestRateText] = useState("0%");
    const [loanAmountText, setLoanAmountText] = useState("₹ 0");
    const [loanTenureText, setLoanTenureText] = useState("0 months");

    // Lead creation and OTP verification states (from HeroSection)
    const [mobileNumber, setMobileNumber] = useState('');
    const [consent, setConsent] = useState(false);
    const [validationErrors, setValidationErrors] = useState({
        mobileNumber: false,
        consent: false
    });
    const [mobileNumberError, setMobileNumberError] = useState('');
    const [showOtpModal, setShowOtpModal] = useState(false);
    const [leadData, setLeadData] = useState(null);
    const [isProcessing, setIsProcessing] = useState(false);
    const [notification, setNotification] = useState({
        show: false,
        message: '',
        type: 'success',
        redirectUrl: null,
        showLoader: false,
        lenderName: null,
        openInNewTab: false
    });

    // Error states for calculator inputs
    const [loanAmountError, setLoanAmountError] = useState('');
    const [interestRateError, setInterestRateError] = useState('');
    const [loanTenureError, setLoanTenureError] = useState('');

    // Editing states for tooltip visibility
    const [isEditingLoanAmount, setIsEditingLoanAmount] = useState(false);
    const [isEditingInterestRate, setIsEditingInterestRate] = useState(false);
    const [isEditingLoanTenure, setIsEditingLoanTenure] = useState(false);
    // Desktop suggestion tooltip flags and timers
    const [showLoanSuggestTip, setShowLoanSuggestTip] = useState(false);
    const [showInterestSuggestTip, setShowInterestSuggestTip] = useState(false);
    const [showTenureSuggestTip, setShowTenureSuggestTip] = useState(false);
    const [showDesktopMobileSuggestTip, setShowDesktopMobileSuggestTip] = useState(false);
    const loanSuggestTimerRef = useRef(null);
    const interestSuggestTimerRef = useRef(null);
    const tenureSuggestTimerRef = useRef(null);
    const mobileSuggestTimerRef = useRef(null);

    // Detect mobile screen to disable suggestion tooltips on mobile
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        const updateIsMobile = () => setIsMobile(window.innerWidth <= 768);
        updateIsMobile();
        window.addEventListener('resize', updateIsMobile);
        return () => window.removeEventListener('resize', updateIsMobile);
    }, []);

    // Fetching Loan Factor
    const fetchLoanFactor = useCallback(async () => {
        try {
            if (!productType) return;
            const payload = { productType };
            const response = await axios.post(`${CREDIT_API_URL}/fetch-loan-factor`, payload);
            const data = response.data.data;
            setLoanFactorApiResponse(data);
            
            // Set default loan amount to 50000 instead of minLoanAmount
            const defaultLoanAmount = 50000;
            setLoanAmount(defaultLoanAmount);
            setInterestRate(data.minROI);
            setLoanTenure(data.minTenure);
            setLoanAmountText(formatToIndianRupee(defaultLoanAmount)); // Fixed: Use defaultLoanAmount instead of data.minLoanAmount
            setInterestRateText(`${data.minROI}%`);
            setLoanTenureText(`${data.minTenure} months`);
        } catch (error) {
            console.error("Error fetching loan factor:", error.response ? error.response.data : error.message);
        }
    }, [productType]);

    useEffect(() => {
        fetchLoanFactor();
    }, [fetchLoanFactor]);



    // Debounced function to call the loan data API
    const fetchLoanData = useCallback(async () => {
        if (isApiCallInProgress) return; // Prevent making multiple API calls simultaneously

        setIsApiCallInProgress(true); // Mark API call as in progress
        try {
            const payload = {
                principal: loanAmount,
                annualRate: interestRate,
                tenure: loanTenure,
            };
            const response = await axios.post(`${CREDIT_API_URL}/calculate-emi`, payload);
            setApiResponse(response.data.data);
        } catch (error) {
            console.error("Error fetching loan data:", error.response ? error.response.data : error.message);
        } finally {
            setIsApiCallInProgress(false); // Mark API call as completed
        }
    }, [loanAmount, interestRate, loanTenure, isApiCallInProgress]);

    // Debounced state change handlers
    const handleLoanAmountChange = (e) => {
        if (!isMobile) {
            setShowLoanSuggestTip(true);
            if (loanSuggestTimerRef.current) clearTimeout(loanSuggestTimerRef.current);
            loanSuggestTimerRef.current = setTimeout(() => setShowLoanSuggestTip(false), 2000);
        }
        const newAmount = parseFloat(e.target.value);
        setLoanAmount(newAmount);
        setLoanAmountText(formatToIndianRupee(newAmount));
        setLoanAmountError(''); // Clear error when slider is used
        debounceApiCall(); // Trigger debounce for API call
    };

    const handleInterestRateChange = (e) => {
        if (!isMobile) {
            setShowInterestSuggestTip(true);
            if (interestSuggestTimerRef.current) clearTimeout(interestSuggestTimerRef.current);
            interestSuggestTimerRef.current = setTimeout(() => setShowInterestSuggestTip(false), 2000);
        }
        const newRate = parseFloat(e.target.value);
        setInterestRate(newRate);
        setInterestRateText(`${newRate}%`);
        setInterestRateError(''); // Clear error when slider is used
        debounceApiCall(); // Trigger debounce for API call
    };

    const handleLoanTenureChange = (e) => {
        if (!isMobile) {
            setShowTenureSuggestTip(true);
            if (tenureSuggestTimerRef.current) clearTimeout(tenureSuggestTimerRef.current);
            tenureSuggestTimerRef.current = setTimeout(() => setShowTenureSuggestTip(false), 2000);
        }
        const newTenure = Number(e.target.value);
        setLoanTenure(newTenure);
        setLoanTenureText(`${newTenure} months`);
        setLoanTenureError(''); // Clear error when slider is used
        debounceApiCall(); // Trigger debounce for API call
    };

    // Debounced function to call the fetchLoanData after delay
    const debounceApiCall = useCallback(() => {
        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            fetchLoanData();
        }, 500);
    }, [fetchLoanData]);

    // Only trigger loan data fetch once after loanFactorApiResponse is available
    useEffect(() => {
        if (loanFactorApiResponse.minLoanAmount !== undefined) {
            fetchLoanData();
        }
    }, [loanFactorApiResponse]);

    // Validation and utility functions (from HeroSection)
    const validateMobileNumber = (mobile) => {
        // Validate 10-digit Indian mobile number
        return /^[6-9]\d{9}$/.test(mobile);
    };

    const handleMobileNumberChange = (e) => {
        let value = e.target.value;
        if (!isMobile && value.length >= 0) {
            setShowDesktopMobileSuggestTip(true);
            if (mobileSuggestTimerRef.current) clearTimeout(mobileSuggestTimerRef.current);
            mobileSuggestTimerRef.current = setTimeout(() => setShowDesktopMobileSuggestTip(false), 2000);
        }
        
        // Remove any non-digit characters
        value = value.replace(/[^\d]/g, '');
        
        // Limit to 10 characters (10 digits)
        if (value.length > 10) {
            value = value.substring(0, 10);
        }
        
        setMobileNumber(value);
        
        // Real-time validation
        if (value) {
            if (validateMobileNumber(value)) {
                setMobileNumberError('');
                setValidationErrors(prev => ({ ...prev, mobileNumber: false }));
            } else {
                setMobileNumberError('Please enter a valid 10-digit mobile number');
                setValidationErrors(prev => ({ ...prev, mobileNumber: true }));
            }
        } else {
            setMobileNumberError('');
            setValidationErrors(prev => ({ ...prev, mobileNumber: false }));
        }
    };

    const handleConsentChange = (e) => {
        const checked = e.target.checked;
        setConsent(checked);
        // Clear validation error when user checks the box
        if (validationErrors.consent) {
            setValidationErrors(prev => ({ ...prev, consent: false }));
        }
    };



    const handleStartApplication = async () => {
        const errors = {
            mobileNumber: !mobileNumber || !validateMobileNumber(mobileNumber),
            consent: !consent
        };

        setValidationErrors(errors);

        // If there are no errors, proceed with lead creation
        if (!errors.mobileNumber && !errors.consent) {
            setIsProcessing(true);
            try {
                // Create lead
                const leadPayload = {
                    name: '', // Optional for emi section
                    mobile: parseInt(mobileNumber),
                    email: '', // Optional for emi section
                    productType: productType,
                    loanAmount: loanAmount,
                    businessName: '', // Optional for emi section
                    isConsentAccepted: consent
                };

                const leadResponse = await axios.post(`${CREDIT_API_URL}/create-lead`, leadPayload);
                
                if (leadResponse.status === 200) {
                    const leadDataFromResponse = leadResponse.data.data;
                    setLeadData(leadDataFromResponse);
                    setStoreLeadData(leadDataFromResponse); // Also set in Zustand store
                    
                    // Generate OTP
                    const otpResponse = await axios.post(`${CREDIT_API_URL}/generate-otp`, {
                        mobile: parseInt(mobileNumber),
                        productType: productType
                    });
                    
                    if (otpResponse.status === 200) {
                        setShowOtpModal(true);
                    } else {
                        throw new Error('OTP generation failed');
                    }
                } else {
                    throw new Error(leadResponse.data.message || 'Lead creation failed');
                }
            } catch (error) {
                console.error('Error in lead creation:', error);
                
                // Handle specific error case: Mobile not verified for this product type
                if (error.response?.status === 400 && 
                    error.response?.data?.message === "Mobile is not verified for this product type. Please verify your mobile number first.") {
                    
                    // Set lead data from the error response
                    if (error.response?.data?.data) {
                        const leadDataFromError = error.response.data.data;
                        setLeadData(leadDataFromError);
                        setStoreLeadData(leadDataFromError); // Also set in Zustand store
                    }
                    
                    // Generate OTP for verification
                    try {
                        const otpResponse = await axios.post(`${CREDIT_API_URL}/generate-otp`, {
                            mobile: parseInt(mobileNumber),
                            productType: productType
                        });
                        
                        if (otpResponse.status === 200) {
                            setShowOtpModal(true);
                        } else {
                            throw new Error('OTP generation failed');
                        }
                    } catch (otpError) {
                        console.error('Error generating OTP:', otpError);
                        setNotification({
                            show: true,
                            message: 'Failed to send OTP. Please try again.',
                            type: 'error',
                            redirectUrl: null,
                            showLoader: false,
                            lenderName: null,
                            openInNewTab: false
                        });
                    }
                } else if (error.response?.status === 409) {
                    // Handle existing lead case
                    const leadDataFromError = error.response?.data?.data;
                    
                    if (leadDataFromError) {
                        // Store lead data and proceed with OTP verification regardless of eligibility
                        setLeadData(leadDataFromError);
                        setStoreLeadData(leadDataFromError); // Also set in Zustand store
                        
                        // Generate OTP for verification
                        try {
                            const otpResponse = await axios.post(`${CREDIT_API_URL}/generate-otp`, {
                                mobile: parseInt(mobileNumber),
                                productType: productType
                            });
                            
                            if (otpResponse.status === 200) {
                                setShowOtpModal(true);
                            } else {
                                throw new Error('OTP generation failed');
                            }
                        } catch (otpError) {
                            console.error('Error generating OTP:', otpError);
                            setNotification({
                                show: true,
                                message: 'Failed to send OTP. Please try again.',
                                type: 'error',
                                redirectUrl: null,
                                showLoader: false,
                                lenderName: null,
                                openInNewTab: false
                            });
                        }
                    } else {
                        // No lead data in error response, show error
                        setNotification({
                            show: true,
                            message: error.response?.data?.message || 'Lead already exists but no data received.',
                            type: 'error',
                            redirectUrl: null,
                            showLoader: false,
                            lenderName: null,
                            openInNewTab: false
                        });
                    }
                } else {
                    // Handle other errors
                    setNotification({
                        show: true,
                        message: error.response?.data?.message || error.message || 'Something went wrong. Please try again.',
                        type: 'error',
                        redirectUrl: null,
                        showLoader: false,
                        lenderName: null,
                        openInNewTab: false
                    });
                }
            } finally {
                setIsProcessing(false);
            }
        }
    };

    // Function to check eligibility and redirect (same as HeroSection)
    const checkEligibilityAndRedirect = useCallback(async (currentLeadId, currentLoanAmount) => {
        try {
            const eligibilityResponse = await axios.get(`${CREDIT_API_URL}/eligibility/check?leadId=${currentLeadId}`);
            const eligibilityData = eligibilityResponse.data;
            console.log('Eligibility Check API Response:', eligibilityData);

            if (eligibilityData && eligibilityData.eligible) {
                // Update leadData in store with latest eligibility data
                setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
                setLeadData(prevLeadData => ({ ...prevLeadData, eligibility: eligibilityData })); // Also update local state

                const { lenderId, applicationStage, lenderLink, lenderName } = eligibilityData;

                if (lenderId === 'CASHE_001') {
                    if (applicationStage === 'leadCreated' || applicationStage === 'personalDetailsFilled') {
                        setStoreCurrentStep(1);
                        setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
                        navigate('/application/journey');
                    } else if (applicationStage === 'preApprovalCheck') {
                        setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
                        setStoreCurrentStep(2);
                        navigate('/application/journey');
                    } else if (applicationStage === 'employmentDetailsFilled') {
                        setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
                        setStoreCurrentStep(2);
                        navigate('/application/journey');
                    // } else if (applicationStage === 'employmentDetailsFilled' || applicationStage === 'documentsUploaded') {
                    //   setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
                    //   setStoreCurrentStep(3);
                    //   navigate('/application/journey');
                    } else if (applicationStage === 'casheCustomerCreated') {
                        setNotification({
                            show: true,
                            message: 'Your application is under progress. You can check latest status on our lending partner\'s website.',
                            type: 'success',
                            redirectUrl: CASHE_URL,
                            showLoader: true,
                            lenderName: 'cashe',
                            openInNewTab: false
                        });
                    } else {
                        setNotification({
                            show: true,
                            message: `Your application is under progress. You can check latest status on our lending partner's website.`,
                            type: 'success',
                            redirectUrl: CASHE_URL,
                            showLoader: true,
                            lenderName: 'cashe',
                            openInNewTab: false
                        });
                    }
                } else if (lenderLink) {
                    // For all other lenders (TrustPaisa, FIMoney, etc.), open in new tab
                    setNotification({
                        show: true,
                        message: `Your application is under progress. You can check latest status on our lending partner's website.`,
                        type: 'success',
                        redirectUrl: lenderLink,
                        showLoader: true,
                        lenderName: lenderName?.toLowerCase().replace(/\s+/g, ''),
                        openInNewTab: true
                    });
                } else {
                    setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
                    navigate('/application/check-eligibility');
                }
            } else {
                // Not eligible, redirect to eligibility page
                setStoreLeadData({ ...(leadData || {}), eligibility: eligibilityData });
                navigate('/application/check-eligibility');
            }
        } catch (error) {
            console.error('Error checking eligibility:', error);
            setNotification({
                show: true,
                message: 'Failed to check eligibility. Please try again.',
                type: 'error',
                redirectUrl: null,
                showLoader: false,
                lenderName: null,
                openInNewTab: false
            });
        }
    }, [leadData, setStoreCurrentStep, setStoreLeadData, navigate, setNotification]);

    const handleOtpSuccess = async () => {
        // Handle successful OTP verification
        if (leadData && leadData.id) {
            setShowOtpModal(false);
            // After successful OTP, check eligibility and redirect
            await checkEligibilityAndRedirect(leadData.id, loanAmount);
            
            // Reset form fields after successful OTP and redirection logic
            setMobileNumber('');
            setConsent(false);
            setLeadData(null); // Clear local leadData after processing
        }
    };

    // Check if all required fields are filled
    const isFormValid = () => {
        const ranges = loanFactorApiResponse || {};
        const isLoanInRange = typeof loanAmount === 'number' && loanAmount >= (ranges.minLoanAmount ?? 0) && loanAmount <= (ranges.maxLoanAmount ?? Number.MAX_SAFE_INTEGER);
        const isRateInRange = typeof interestRate === 'number' && interestRate >= (ranges.minROI ?? 0) && interestRate <= (ranges.maxROI ?? Number.MAX_SAFE_INTEGER);
        const isTenureInRange = typeof loanTenure === 'number' && loanTenure >= (ranges.minTenure ?? 0) && loanTenure <= (ranges.maxTenure ?? Number.MAX_SAFE_INTEGER);

        const isMobileValid = mobileNumber && validateMobileNumber(mobileNumber);
        const hasNoErrors = !loanAmountError && !interestRateError && !loanTenureError && !mobileNumberError;

        return isLoanInRange && isRateInRange && isTenureInRange && isMobileValid && consent && hasNoErrors;
    };



    const handleLoanAmountInput = (e) => {
        setIsEditingLoanAmount(true);
        const value = e.target.innerText.trim().replace(/[₹,]/g, '').trim();
        const parsedValue = parseInt(value);
        const selection = window.getSelection();
        const range = selection?.getRangeAt(0);

        if (debounceTimeout.current) {
            clearTimeout(debounceTimeout.current);
        }

        debounceTimeout.current = setTimeout(() => {
            if (!isNaN(parsedValue)) {
                if (parsedValue >= loanFactorApiResponse.minLoanAmount && parsedValue <= loanFactorApiResponse.maxLoanAmount) {
                    setLoanAmount(parsedValue);
                    setLoanAmountText(formatToIndianRupee(parsedValue));
                    setLoanAmountError(''); // Clear error
                    debounceApiCall();
                    
                    // Move cursor to end after update
                    setTimeout(() => {
                        const element = e.target;
                        if (element) {
                            const range = document.createRange();
                            const selection = window.getSelection();
                            range.selectNodeContents(element);
                            range.collapse(false); // false means collapse to end
                            selection.removeAllRanges();
                            selection.addRange(range);
                        }
                    }, 0);
                } else {
                    // Show error message for out of range values
                    if (parsedValue < loanFactorApiResponse.minLoanAmount) {
                        setLoanAmountError(`Please enter amount between ${formatToIndianRupee(loanFactorApiResponse.minLoanAmount)} and ${formatToIndianRupee(loanFactorApiResponse.maxLoanAmount)}`);
                    } else if (parsedValue > loanFactorApiResponse.maxLoanAmount) {
                        setLoanAmountError(`Please enter amount between ${formatToIndianRupee(loanFactorApiResponse.minLoanAmount)} and ${formatToIndianRupee(loanFactorApiResponse.maxLoanAmount)}`);
                    }
                    
                    // Move cursor to end after showing error
                    setTimeout(() => {
                        const element = e.target;
                        if (element) {
                            const range = document.createRange();
                            const selection = window.getSelection();
                            range.selectNodeContents(element);
                            range.collapse(false); // false means collapse to end
                            selection.removeAllRanges();
                            selection.addRange(range);
                        }
                    }, 0);
                }
            } else {
                setLoanAmountText(formatToIndianRupee(loanAmount));
                setLoanAmountError('Please enter a valid amount');
                
                // Move cursor to end after reverting
                setTimeout(() => {
                    const element = e.target;
                    if (element) {
                        const range = document.createRange();
                        const selection = window.getSelection();
                        range.selectNodeContents(element);
                        range.collapse(false); // false means collapse to end
                        selection.removeAllRanges();
                        selection.addRange(range);
                    }
                }, 0);
            }
        }, 300);
    };

    const handleInterestRateInput = (e) => {
        setIsEditingInterestRate(true);
        let value = e.target.innerText.trim();
        value = value.replace('%', '').trim();
        const parsedValue = parseFloat(value);

        if (!isNaN(parsedValue)) {
            if (parsedValue >= loanFactorApiResponse.minROI && parsedValue <= loanFactorApiResponse.maxROI) {
                setInterestRate(parsedValue);
                setInterestRateText(`${parsedValue}%`);
                setInterestRateError(''); // Clear error
                debounceApiCall();
            } else {
                // Show error message for out of range values
                if (parsedValue < loanFactorApiResponse.minROI) {
                    setInterestRateError(`Please enter interest rate between ${loanFactorApiResponse.minROI}% and ${loanFactorApiResponse.maxROI}%`);
                } else if (parsedValue > loanFactorApiResponse.maxROI) {
                    setInterestRateError(`Please enter interest rate between ${loanFactorApiResponse.minROI}% and ${loanFactorApiResponse.maxROI}%`);
                }
                setInterestRateText(`${interestRate}%`);
            }
        } else {
            setInterestRateText(`${interestRate}%`);
            setInterestRateError('Please enter a valid interest rate');
        }
    };

    const handleLoanTenureInput = (e) => {
        setIsEditingLoanTenure(true);
        let value = e.target.innerText.trim();
        value = value.replace('months', '').trim();
        const parsedValue = parseInt(value);

        if (!isNaN(parsedValue)) {
            if (parsedValue >= loanFactorApiResponse.minTenure && parsedValue <= loanFactorApiResponse.maxTenure) {
                setLoanTenure(parsedValue);
                setLoanTenureText(`${parsedValue} months`);
                setLoanTenureError(''); // Clear error
                debounceApiCall();
            } else {
                // Show error message for out of range values
                if (parsedValue < loanFactorApiResponse.minTenure) {
                    setLoanTenureError(`Please enter tenure between ${loanFactorApiResponse.minTenure} and ${loanFactorApiResponse.maxTenure} months`);
                } else if (parsedValue > loanFactorApiResponse.maxTenure) {
                    setLoanTenureError(`Please enter tenure between ${loanFactorApiResponse.minTenure} and ${loanFactorApiResponse.maxTenure} months`);
                }
                setLoanTenureText(`${loanTenure} months`);
            }
        } else {
            setLoanTenureText(`${loanTenure} months`);
            setLoanTenureError('Please enter a valid tenure');
        }
    };

    const interestPayable = apiResponse.totalInterest || 0;

    const data = {
        labels: ["Interest Payable", "Loan Amount"],
        datasets: [
            {
                data: [interestPayable, loanAmount],
                backgroundColor: ['#283782', '#627DD4'],
                borderWidth: 0,
            },
        ],
    };

    const options = {
        plugins: {
            legend: {
                display: false,
            },
            tooltip: {
                enabled: true,
                position: 'nearest',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                titleColor: '#ffffff',
                bodyColor: '#ffffff',
                borderColor: '#ffffff',
                borderWidth: 1,
                padding: 5,
                displayColors: false,
                callbacks: {
                    label: function (tooltipItem) {
                        const value = tooltipItem.raw;
                        return `₹ ${value.toLocaleString('en-IN')}`;
                    },
                },
            },
        },
        cutout: '65%'
    };

    return (
        <Container className="emi-section-container mt-3">
            <Row className="justify-content-center" ref={ref}>
                <Col lg={12} className="emi-section-main mt-2 rounded">
                    <Row className="emi-section-bottom">
                        <Col md={6} className="emi-section-bottom-left d-flex flex-column justify-content-around p-2">
                            <div className="emi-section-bottom-left-step">
                                <div className="emi-section-bottom-left-step-top d-flex justify-content-between align-items-center mb-1">
                                    <label htmlFor="loan-amount" className="mb-0">
                                        <span>{t('emi-section-step1')}</span>
                                    </label>
                                                                                                                <OverlayTrigger
                                        placement="right"
                                        trigger="manual"
                                        show={!isMobile && showLoanSuggestTip}
                                        overlay={
                                            <BSTooltip className="homepage-suggestion-tooltip">
                                                Enter amount between {formatToIndianRupee(loanFactorApiResponse.minLoanAmount)} and {formatToIndianRupee(loanFactorApiResponse.maxLoanAmount)}
                                            </BSTooltip>
                                        }
                                    >
                                        <div
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={handleLoanAmountInput}
                                            onFocus={() => setIsEditingLoanAmount(true)}
                                            onBlur={() => setIsEditingLoanAmount(false)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleLoanAmountInput(e);
                                                }
                                                // Prevent typing more than 10 characters
                                                if (e.target.textContent && e.target.textContent.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                                                    e.preventDefault();
                                                }
                                            }}
                                            className={`emi-section-bottom-left-step-top-value ${loanAmountError ? 'homepage-input-field-error' : ''}`}
                                        >
                                            {loanAmountText}
                                        </div>
                                    </OverlayTrigger>
                                </div>
                                <Form.Range
                                    id="loan-amount"
                                    min={loanFactorApiResponse.minLoanAmount}
                                    max={loanFactorApiResponse.maxLoanAmount}
                                    step={1000}
                                    value={loanAmount}
                                    onChange={handleLoanAmountChange}
                                />
                                <div className="range-slider-container d-flex justify-content-between">
                                    <label htmlFor="loan-amount" className="range-label min-label">{formatToIndianRupee(loanFactorApiResponse.minLoanAmount)}</label>
                                    <label htmlFor="loan-amount" className="range-label max-label">{formatToIndianRupee(loanFactorApiResponse.maxLoanAmount)}</label>
                                </div>
                            </div>

                            <div className="emi-section-bottom-left-step">
                                <div className="emi-section-bottom-left-step-top d-flex justify-content-between align-items-center mb-1">
                                    <label htmlFor="loan-interest" className="mb-0">
                                        <span>{t('emi-section-step2')}</span>
                                    </label>
                                    <OverlayTrigger
                                        placement="right"
                                        trigger="manual"
                                        show={!isMobile && showInterestSuggestTip}
                                        overlay={
                                            <BSTooltip className="homepage-suggestion-tooltip">
                                                Enter ROI between {loanFactorApiResponse.minROI}% and {loanFactorApiResponse.maxROI}%
                                            </BSTooltip>
                                        }
                                    >
                                        <div
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={handleInterestRateInput}
                                            onFocus={() => setIsEditingInterestRate(true)}
                                            onBlur={() => setIsEditingInterestRate(false)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleInterestRateInput(e);
                                                }
                                                // Prevent typing more than 10 characters
                                                if (e.target.textContent && e.target.textContent.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                                                    e.preventDefault();
                                                }
                                            }}
                                            className={`emi-section-bottom-left-step-top-value ${interestRateError ? 'homepage-input-field-error' : (interestRate && interestRate >= (loanFactorApiResponse?.minROI || 0) && interestRate <= (loanFactorApiResponse?.maxROI || 0) ? 'homepage-input-field-valid' : '')}`}
                                        >
                                            {interestRateText}
                                        </div>
                                    </OverlayTrigger>
                                </div>
                                <Form.Range
                                    id="loan-interest"
                                    min={loanFactorApiResponse.minROI}
                                    max={loanFactorApiResponse.maxROI}
                                    step={0.25}
                                    value={interestRate}
                                    onChange={handleInterestRateChange}
                                />
                                <div className="range-slider-container d-flex justify-content-between">
                                    <label htmlFor="loan-interest" className="range-label min-label">{loanFactorApiResponse.minROI} %</label>
                                    <label htmlFor="loan-interest" className="range-label max-label">{loanFactorApiResponse.maxROI} %</label>
                                </div>
                            </div>

                            <div className="emi-section-bottom-left-step">
                                <div className="emi-section-bottom-left-step-top d-flex justify-content-between align-items-center mb-1">
                                    <label htmlFor="loan-tenure" className="mb-0">
                                        <span>{t('emi-section-step3')}</span>
                                    </label>
                                    <OverlayTrigger
                                        placement="right"
                                        trigger="manual"
                                        show={!isMobile && showTenureSuggestTip}
                                        overlay={
                                            <BSTooltip className="homepage-suggestion-tooltip">
                                                Enter tenure between {loanFactorApiResponse.minTenure} and {loanFactorApiResponse.maxTenure} {t('emi-section-tenure')}
                                            </BSTooltip>
                                        }
                                    >
                                        <div
                                            contentEditable
                                            suppressContentEditableWarning
                                            onInput={handleLoanTenureInput}
                                            onFocus={() => setIsEditingLoanTenure(true)}
                                            onBlur={() => setIsEditingLoanTenure(false)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') {
                                                    e.preventDefault();
                                                    handleLoanTenureInput(e);
                                                }
                                                // Prevent typing more than 10 characters
                                                if (e.target.textContent && e.target.textContent.length >= 10 && e.key !== 'Backspace' && e.key !== 'Delete' && e.key !== 'ArrowLeft' && e.key !== 'ArrowRight' && e.key !== 'Tab') {
                                                    e.preventDefault();
                                                }
                                            }}
                                            className={`emi-section-bottom-left-step-top-value ${loanTenureError ? 'homepage-input-field-error' : ''}`}
                                        >
                                            {loanTenureText}
                                        </div>
                                    </OverlayTrigger>
                                </div>
                                <Form.Range
                                    id="loan-tenure"
                                    min={loanFactorApiResponse.minTenure}
                                    max={loanFactorApiResponse.maxTenure}
                                    value={loanTenure}
                                    onChange={handleLoanTenureChange}
                                />
                                <div className="range-slider-container d-flex justify-content-between">
                                    <label htmlFor="loan-tenure" className="range-label min-label">{loanFactorApiResponse.minTenure} {t('emi-section-tenure')}</label>
                                    <label htmlFor="loan-tenure" className="range-label max-label">{loanFactorApiResponse.maxTenure} {t('emi-section-tenure')}</label>
                                </div>
                            </div>
                        </Col>

                        <Col md={6} className="emi-section-bottom-right d-flex flex-column justify-content-center align-items-center p-2">
                            <div className="emi-section-bottom-right-top text-center">
                                <div className="emi-section-bottom-right-top-chart position-relative d-inline-block">
                                    <img src="/images/rupee-emi-section.png" alt="" className="position-absolute translate-middle" />
                                    <div style={{ width: '100%', height: '100%' }}>
                                        <Doughnut
                                            data={data}
                                            options={options}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="emi-section-bottom-right-bottom p-2 ">
                                <div className="emi-section-bottom-right-bottom-content d-flex justify-content-between align-items-center">
                                    <div className="emi-section-label d-flex align-items-center">
                                        <div className="emi-section-label-img me-2"><img src="/images/light-blue-circle.png" alt="" className="img-fluid" /></div>
                                        <div className="emi-section-label-title">{t('emi-section-result1')}</div>
                                    </div>
                                    <div className="emi-section-bottom-right-bottom-content-value">{loanAmountText}</div>
                                </div>
                                <div className="emi-section-bottom-right-bottom-content d-flex justify-content-between align-items-center">
                                    <div className="emi-section-label d-flex align-items-center">
                                        <div className="emi-section-label-img me-2"><img src="/images/dark-blue-circle.png" alt="" className="img-fluid" /></div>
                                        <div className="emi-section-label-title">{t('emi-section-result2')}</div>
                                    </div>
                                    <div className="emi-section-bottom-right-bottom-content-value">{formatToIndianRupee(apiResponse.totalInterest)}</div>
                                </div>
                                <hr className="my-2" />
                                <div className="emi-section-bottom-right-bottom-content d-flex justify-content-between align-items-center">
                                    <div className="emi-section-bottom-right-bottom-content-title fw-bold">{t('emi-section-result3')}</div>
                                    <div className="emi-section-bottom-right-bottom-content-value fw-bold"> {formatToIndianRupee(apiResponse.emi)}</div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    <Row className="justify-content-center g-3 emi-form-section-bottom">
                        <Col md={12} lg={6} className="emi-form d-none d-lg-block">
                            <div className="emi-form-content">
                                {/* First Row: Mobile Number Input Field and Apply Now Button */}
                                <div className="row align-items-center mb-2">
                                    <div className="col-md-7">
                                        <div className="emi-homepage-input-field-container">
                                            <OverlayTrigger
                                            placement="top"
                                            trigger="manual"
                                            show={!isMobile && showDesktopMobileSuggestTip}
                                                overlay={
                                                    <BSTooltip className="homepage-suggestion-tooltip">
                                                        Enter 10 digit number starting with 6, 7, 8, or 9
                                                    </BSTooltip>
                                                }
                                            >
                                                <div className={`homepage-input-field ${mobileNumberError ? 'homepage-input-field-error' : ''}`}>
                                                    <span className="homepage-input-prefix">+91</span>
                                                    <input
                                                        type="tel"
                                                        placeholder=""
                                                        value={mobileNumber}
                                                        onChange={handleMobileNumberChange}
                                                        className="homepage-input-field-text"
                                                        maxLength="10"
                                                        inputMode="numeric"
                                                        pattern="[0-9]*"
                                                    />
                                                    <div className="homepage-hero-placeholder">Enter Your Mobile Number</div>
                                                </div>
                                            </OverlayTrigger>
                                        </div>
                                    </div>
                                    <div className="col-md-5">
                                        {!isFormValid() && !isProcessing ? (
                                            <OverlayTrigger
                                                placement="bottom"
                                                trigger={['hover', 'click']}
                                                delay={{ show: 0, hide:0 }}
                                                overlay={
                                                    <BSTooltip className="error-tooltip">
                                                        Please fill all required details to proceed
                                                    </BSTooltip>
                                                }
                                            >
                                                <span>
                                                    <button 
                                                        className="emi-homepage-start-application-btn w-100 mx-1"
                                                        onClick={handleStartApplication}
                                                        disabled={isProcessing || !isFormValid()}
                                                    >
                                                        {isProcessing ? 'Processing...' : 'Start Application'}
                                                    </button>
                                                </span>
                                            </OverlayTrigger>
                                        ) : (
                                            <button 
                                                className="emi-homepage-start-application-btn w-100 mx-1"
                                                onClick={handleStartApplication}
                                                disabled={isProcessing || !isFormValid()}
                                            >
                                                {isProcessing ? 'Processing...' : 'Start Application'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Col>

                        {/* Mobile Form */}
                        <Col sm={12} className="emi-form-mobile d-block d-lg-none mt-3">
                            <div className="emi-form-mobile-content align-items-center justify-content-center">
                                {/* First Row: Mobile Number Input Field and Apply Now Button */}
                                <div className="row align-items-center justify-content-center">
                                    <div className="col-12">
                                        <div className="homepage-input-field-container">
                                            <div className={`homepage-input-field ${validationErrors.mobileNumber ? 'homepage-input-field-error' : ''}`}>
                                                <span className="homepage-input-prefix">+91</span>
                                                <input
                                                    type="tel"
                                                    placeholder=""
                                                    value={mobileNumber}
                                                    onChange={handleMobileNumberChange}
                                                    className="homepage-input-field-text"
                                                    maxLength="10"
                                                    inputMode="numeric"
                                                    pattern="[0-9]*"
                                                />
                                                <div className="homepage-hero-placeholder">Enter Your Mobile Number</div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="homepage-consent-section col-12">
                                <div className="homepage-consent-content">
                                    <input
                                        type="checkbox"
                                        checked={consent}
                                        onChange={handleConsentChange}
                                        className="homepage-consent-checkbox"
                                    />
                                    <span className="homepage-consent-text">
                                        I allow Snapfunds to send an OTP for my mobile number verification.
                                        I also allow SnapFunds and its loan partner to share me details regarding the loan application process and available offers.
                                    </span>
                                </div>
                            </div>
                                        <div className="col-6">
                                        {!isFormValid() && !isProcessing ? (
                                            <OverlayTrigger
                                                placement="bottom"
                                                trigger={['hover', 'click']}
                                                delay={{ show: 0, hide: 1000 }}
                                                overlay={
                                                    <BSTooltip className="error-tooltip">
                                                        Please fill all required details to proceed
                                                    </BSTooltip>
                                                }
                                            >
                                                <span>
                                                    <button 
                                                        className="emi-homepage-start-application-btn w-100"
                                                        onClick={handleStartApplication}
                                                        disabled={isProcessing || !isFormValid()}
                                                    >
                                                        {isProcessing ? 'Processing...' : 'Start Application'}
                                                    </button>
                                                </span>
                                            </OverlayTrigger>
                                        ) : (
                                            <button 
                                                className="emi-homepage-start-application-btn w-100"
                                                onClick={handleStartApplication}
                                                disabled={isProcessing || !isFormValid()}
                                            >
                                                {isProcessing ? 'Processing...' : 'Start Application'}
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </Col>
                    </Row>

                    {/* Consent Section - Separate Row */}
                    <Row className="justify-content-center emi-form-consent-section">
                        <Col md={12} lg={8}>
                            <div className="homepage-consent-section">
                                <div className="homepage-consent-content">
                                    <input
                                        type="checkbox"
                                        checked={consent}
                                        onChange={handleConsentChange}
                                        className="homepage-consent-checkbox"
                                    />
                                    <span className="homepage-consent-text">
                                        I allow Snapfunds to send an OTP for my mobile number verification.
                                        I also allow SnapFunds and its loan partner to share me details regarding the loan application process and available offers.
                                    </span>
                                </div>
                            </div>
                        </Col>
                    </Row>
                </Col>
            </Row>

            {/* OTP Verification Modal */}
            <OtpVerificationModal
                show={showOtpModal}
                mobile={mobileNumber}
                productType={productType}
                onClose={() => setShowOtpModal(false)}
                onVerified={handleOtpSuccess}
            />
            
            {/* Notification Popup */}
            <NotificationPopup
                show={notification.show}
                message={notification.message}
                type={notification.type}
                onClose={() => setNotification({ 
                    show: false, 
                    message: '', 
                    type: 'success',
                    redirectUrl: null,
                    showLoader: false,
                    lenderName: null,
                    openInNewTab: false
                })}
                redirectUrl={notification.redirectUrl}
                showLoader={notification.showLoader}
                lenderName={notification.lenderName}
                openInNewTab={notification.openInNewTab}
            />
        </Container>
    );
});

export default EmiSection;